/**
 * External Payments Hub API Client
 * Handles requests to the hub summary and lease external status endpoints
 */

import { z } from 'zod';
import { apiFetch } from '../lib/api';
import {
  LeaseExternalStatusSchema,
  PropertySetupStatusSchema,
  HubPendingMatchSchema,
  HubAutoAppliedPaymentSchema,
  HubConnectionAlertSchema,
  IgnoreRuleResponseSchema,
  ExternalPaymentHubSummaryResponseSchema,
  UnmatchedTransactionSchema,
  GetUnmatchedTransactionsCountResponseSchema,
  type LeaseExternalPaymentStatusResponse,
} from '@resida/api-types/payments';

// ---------------------------------------------------------------------------
// Hub Summary Types
// ---------------------------------------------------------------------------

// Derived from LeaseExternalStatusSchema in @resida/api-types/payments
export type LeaseExternalStatus = z.infer<typeof LeaseExternalStatusSchema>;

// Derived from PropertySetupStatusSchema in @resida/api-types/payments
export type PropertySetupStatus = z.infer<typeof PropertySetupStatusSchema>;

// Derived from HubPendingMatchSchema in @resida/api-types/payments
export type HubPendingMatch = z.infer<typeof HubPendingMatchSchema>;

// Derived from HubAutoAppliedPaymentSchema in @resida/api-types/payments
export type HubAutoAppliedPayment = z.infer<typeof HubAutoAppliedPaymentSchema>;

// Derived from HubConnectionAlertSchema in @resida/api-types/payments
export type HubConnectionAlert = z.infer<typeof HubConnectionAlertSchema>;

export interface LinkedAccountSummary {
  id: string;
  institutionName: string;
  accountMask: string;
  accountName: string;
  accountType: string;
  status: string;
  lastSyncedAt: string | null;
}

// Derived from UnmatchedTransactionSchema in @resida/api-types/payments.
//
// api-types-922 follow-up: this used to be a hand-written interface with a `plaidCategory:
// string | null` field that has no counterpart in UnmatchedTransactionSchema. Investigated and
// confirmed that's not a declaration gap: GET /landlord/api/external-payments/hub-summary's
// handler (getHubSummary in apps/api/src/services/external-payment-hub.service.ts) never
// includes plaidCategory (or reads it from the DB) when building unmatchedTransactions in the
// first place — a *different* function (getUnmatchedIncomingDeposits in
// apps/api/src/services/payments/bank-transaction.service.ts) computes plaidCategory, but only
// for internal platform-payment detection, never for this response. No frontend code read
// `.plaidCategory` off this type, so it was dead. Dropped here rather than added to the schema,
// which would have falsely implied the server already sends it.
export type UnmatchedTransaction = z.infer<typeof UnmatchedTransactionSchema>;

// ---------------------------------------------------------------------------
// Ignore Rules
// ---------------------------------------------------------------------------

// Derived from IgnoreRuleResponseSchema in @resida/api-types/payments
export type IgnoreRule = z.infer<typeof IgnoreRuleResponseSchema>;

// Derived from ExternalPaymentHubSummaryResponseSchema in @resida/api-types/payments
export type ExternalPaymentHubSummary = z.infer<typeof ExternalPaymentHubSummaryResponseSchema>;

export async function fetchExternalPaymentsHubSummary(): Promise<ExternalPaymentHubSummary> {
  return apiFetch<ExternalPaymentHubSummary>('/landlord/api/external-payments/hub-summary');
}

// R-EXT-PAY-BADGE — External Payments nav badge count. Derived from
// GetUnmatchedTransactionsCountResponseSchema in @resida/api-types/payments — the same
// schema object GET /landlord/api/external-payments/unmatched/count
// (external-payments.routes.ts) serializes the response through.
export type GetUnmatchedTransactionsCountResponse = z.infer<
  typeof GetUnmatchedTransactionsCountResponseSchema
>;

export async function fetchUnmatchedTransactionsCount(): Promise<GetUnmatchedTransactionsCountResponse> {
  return apiFetch<GetUnmatchedTransactionsCountResponse>(
    '/landlord/api/external-payments/unmatched/count'
  );
}

// ---------------------------------------------------------------------------
// Lease External Payment Status Types
// ---------------------------------------------------------------------------

// Derived from LeaseExternalPaymentStatusResponseSchema in @resida/api-types/payments (AC-14) —
// this used to be a hand-written interface (with a `pendingMatch: PendingMatchDetail | null`
// field and an unused `activationReadiness` array the service never populated). The boundary
// route now returns `pendingMatches` (all pending suggested matches, newest first, capped) plus
// `pendingMatchCount` (the true uncapped total) instead of a single most-recent match — kept as
// an alias so existing import sites (LeaseDetailPage, CurrentPeriodPaymentCard) need no edit.
export type LeaseMatchingSummary = LeaseExternalPaymentStatusResponse;

/**
 * AC-14 regression guard. `LeaseMatchingSummary` must stay an ALIAS of the api-types boundary
 * contract, never drift back into a hand-maintained re-declaration (CLAUDE.md: boundary types
 * live in `packages/api-types` and are never redeclared in the UI — that duplication is exactly
 * what Change 19 removed here). The mutual-assignability assertion below fails
 * `turbo run typecheck` the moment the two shapes diverge, so a future local re-declaration
 * cannot silently drift from the endpoint it claims to describe.
 */
type MutuallyAssignable<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
const leaseMatchingSummaryMatchesContract: MutuallyAssignable<
  LeaseMatchingSummary,
  LeaseExternalPaymentStatusResponse
> = true;
void leaseMatchingSummaryMatchesContract;

export async function fetchLeaseMatchingSummary(leaseId: string): Promise<LeaseMatchingSummary> {
  return apiFetch<LeaseMatchingSummary>(
    `/landlord/api/leases/${leaseId}/external-payment-status`
  );
}

// ---------------------------------------------------------------------------
// Match Actions
// ---------------------------------------------------------------------------

export async function confirmExternalPaymentMatch(transactionId: string): Promise<void> {
  await apiFetch<void>(
    `/landlord/api/manager/external-payments/transactions/${transactionId}/confirm`,
    { method: 'POST' }
  );
}

export async function dismissExternalPaymentTransaction(transactionId: string): Promise<void> {
  await apiFetch<void>(
    `/landlord/api/manager/external-payments/transactions/${transactionId}/dismiss`,
    { method: 'POST' }
  );
}

export async function manualMatchTransaction(
  transactionId: string,
  leaseId: string,
  tenantId: string
): Promise<void> {
  await apiFetch<void>(
    `/landlord/api/manager/external-payments/transactions/${transactionId}/match`,
    {
      method: 'POST',
      body: JSON.stringify({ leaseId, tenantId }),
    }
  );
}

// ---------------------------------------------------------------------------
// Active Leases (for lease picker in manual match)
// ---------------------------------------------------------------------------

export interface ActiveLeaseSummary {
  id: string;
  unitId: string;
  unitNumber: string | null;
  address1: string;
  propertyName: string;
  tenants: Array<{ id: string; firstName: string; lastName: string }>;
  rentAmount: number;
  state: string;
}

export async function fetchActiveLeases(): Promise<ActiveLeaseSummary[]> {
  const response = await apiFetch<{
    leases: Array<{
      id: string;
      unitId: string;
      unitNumber: string | null;
      address1: string;
      propertyName: string;
      state: string;
      rentAmount: number;
      tenants: Array<{ id: string; firstName: string; lastName: string }>;
    }>;
  }>('/landlord/api/leases?state=active');
  return response.leases.map((l) => ({
    id: l.id,
    unitId: l.unitId,
    unitNumber: l.unitNumber,
    address1: l.address1,
    propertyName: l.propertyName,
    tenants: l.tenants,
    rentAmount: l.rentAmount,
    state: l.state,
  }));
}

// ---------------------------------------------------------------------------
// Ignore Transaction / Ignore Rules
// ---------------------------------------------------------------------------

export async function ignoreTransaction(
  transactionId: string,
  rule: { field: string; operator: string; value: string; label?: string }
): Promise<{ ruleId: string; retroactiveCount: number }> {
  return apiFetch(
    `/landlord/api/manager/external-payments/transactions/${transactionId}/ignore`,
    { method: 'POST', body: JSON.stringify(rule) }
  );
}

export async function fetchIgnoreRules(): Promise<IgnoreRule[]> {
  const data = await apiFetch<{ rules: IgnoreRule[] }>(
    '/landlord/api/manager/external-payments/transaction-ignore-rules'
  );
  return data.rules;
}

export async function updateIgnoreRule(
  ruleId: string,
  patch: Partial<Pick<IgnoreRule, 'field' | 'operator' | 'value'>>
): Promise<IgnoreRule> {
  return apiFetch(
    `/landlord/api/manager/external-payments/transaction-ignore-rules/${ruleId}`,
    { method: 'PATCH', body: JSON.stringify(patch) }
  );
}

export async function deleteIgnoreRule(ruleId: string): Promise<void> {
  await apiFetch(
    `/landlord/api/manager/external-payments/transaction-ignore-rules/${ruleId}`,
    { method: 'DELETE' }
  );
}
